import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db, storage } from '../../firebase/config';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import toast from 'react-hot-toast';
import './BrandAdminPanel.css';

const CATEGORIES = ['Breakfast', 'Morning Tea', 'Lunch', 'Platters', 'Salads', 'Treats', 'Drinks'];

const BrandAdminPanel = () => {
  const { slug } = useParams();
  const { brandAdminSlug, superAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [activeTab, setActiveTab] = useState('menu');

  useEffect(() => {
    if (brandAdminSlug !== slug && !superAdmin) { navigate(`/${slug}/admin/login`); return; }
    const fetchBrand = async () => {
      try {
        const snap = await getDoc(doc(db, 'brands', slug));
        if (snap.exists()) setBrand({ id: snap.id, ...snap.data() });
      } catch {}
    };
    fetchBrand();
  }, [slug, brandAdminSlug, superAdmin, navigate]);

  if (!brand) return <div className="bap-loading">Loading...</div>;

  const primary = brand.primaryColor || '#111';

  return (
    <div className="bap-page">
      <div className="bap-sidebar" style={{ background: primary }}>
        <div className="bap-logo">
          {brand.logoUrl ? <img src={brand.logoUrl} alt={brand.name} className="bap-logo-img" /> : <div className="bap-logo-initials">{brand.name?.slice(0,2).toUpperCase()}</div>}
          <div>
            <div className="bap-brand-name">{brand.name}</div>
            <div className="bap-brand-sub">Admin Panel</div>
          </div>
        </div>
        <nav className="bap-nav">
          {[
            { id: 'menu', label: 'Menu Items', icon: '🍽️' },
            { id: 'stores', label: 'Stores', icon: '🏪' },
            { id: 'emailjs', label: 'Email Settings', icon: '✉️' },
          ].map(t => (
            <button key={t.id} className={`bap-nav-btn ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
              <span>{t.icon}</span>{t.label}
            </button>
          ))}
        </nav>
        <div className="bap-sidebar-footer">
          <a href={`/${slug}`} target="_blank" rel="noreferrer" className="bap-view-site">↗ View Site</a>
          {superAdmin && <a href="/superadmin" className="bap-view-site">← Super Admin</a>}
          <button className="bap-logout" onClick={() => { logout(); navigate(`/${slug}`); }}>Sign Out</button>
        </div>
      </div>
      <div className="bap-content">
        {activeTab === 'menu' && <BrandMenuManager slug={slug} />}
        {activeTab === 'stores' && <BrandStoreManager slug={slug} />}
        {activeTab === 'emailjs' && <BrandEmailSettings slug={slug} />}
      </div>
    </div>
  );
};

const BrandMenuManager = ({ slug }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchItems = async () => {
    try {
      const snap = await getDocs(collection(db, 'brands', slug, 'menuItems'));
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setItems(all.sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999)));
    } catch { setItems([]); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchItems(); }, [slug]);

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await deleteDoc(doc(db, 'brands', slug, 'menuItems', item.id));
      if (item.storagePath) { try { await deleteObject(ref(storage, item.storagePath)); } catch {} }
      toast.success('Deleted');
      fetchItems();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="bap-section">
      <div className="bap-section-header">
        <div><h2>Menu Items</h2><p>{items.length} items</p></div>
        <button className="btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>+ Add Item</button>
      </div>
      {showForm && <BrandMenuForm slug={slug} editing={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { fetchItems(); setShowForm(false); setEditing(null); }} />}
      {loading ? <p style={{ color: 'var(--grey-mid)' }}>Loading...</p> : (
        <div className="bap-items-grid">
          {items.map(item => (
            <div key={item.id} className="bap-item-card">
              <div className="bap-item-img">
                {item.imageUrl ? <img src={item.imageUrl} alt={item.name} /> : <div className="bap-item-placeholder">No image</div>}
                <span className="bap-item-cat">{item.category}</span>
                {item.sortOrder && item.sortOrder !== 9999 && <span className="bap-item-sort">#{item.sortOrder}</span>}
              </div>
              <div className="bap-item-body">
                <h3>{item.name}</h3>
                <p>{item.description}</p>
                <div className="bap-item-footer">
                  <span className="bap-item-price">${parseFloat(item.price).toFixed(2)}</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn-outline" style={{ padding: '6px 12px', fontSize: 11 }} onClick={() => { setEditing(item); setShowForm(true); }}>Edit</button>
                    <button className="btn-danger" onClick={() => handleDelete(item)}>Delete</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="bap-empty">No items yet. Add your first menu item!</p>}
        </div>
      )}
    </div>
  );
};

const BrandMenuForm = ({ slug, editing, onClose, onSaved }) => {
  const fileRef = useRef();
  const [form, setForm] = useState({ name: editing?.name || '', description: editing?.description || '', price: editing?.price || '', category: editing?.category || CATEGORIES[0], sortOrder: editing?.sortOrder ?? '' });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(editing?.imageUrl || null);
  const [saving, setSaving] = useState(false);

  const handleFile = (e) => { const f = e.target.files[0]; if (!f) return; setImageFile(f); setImagePreview(URL.createObjectURL(f)); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    if (!form.price || isNaN(form.price)) { toast.error('Valid price required'); return; }
    setSaving(true);
    try {
      let imageUrl = editing?.imageUrl || '';
      let storagePath = editing?.storagePath || '';
      if (imageFile) {
        const path = `brands/${slug}/menu/${Date.now()}_${imageFile.name}`;
        const sRef = ref(storage, path);
        await uploadBytes(sRef, imageFile);
        imageUrl = await getDownloadURL(sRef);
        storagePath = path;
        if (editing?.storagePath) { try { await deleteObject(ref(storage, editing.storagePath)); } catch {} }
      }
      const data = { ...form, price: parseFloat(form.price), sortOrder: form.sortOrder !== '' ? parseInt(form.sortOrder) : 9999, imageUrl, storagePath };
      if (editing) {
        await updateDoc(doc(db, 'brands', slug, 'menuItems', editing.id), data);
        toast.success('Updated!');
      } else {
        await addDoc(collection(db, 'brands', slug, 'menuItems'), { ...data, createdAt: serverTimestamp() });
        toast.success('Added!');
      }
      onSaved();
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSaving(false);
  };

  return (
    <div className="bap-form-card">
      <div className="bap-form-header"><h3>{editing ? 'Edit Item' : 'Add New Item'}</h3><button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--grey-mid)' }}>×</button></div>
      <div className="bap-form-grid">
        <div className="form-group"><label className="form-label">Name *</label><input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Club Sandwich Platter" /></div>
        <div className="form-group"><label className="form-label">Price (AUD) *</label><input className="form-input" type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} placeholder="e.g. 45.00" /></div>
        <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">Description</label><textarea className="form-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Describe the item..." rows={3} /></div>
        <div className="form-group"><label className="form-label">Category</label><select className="form-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></div>
        <div className="form-group"><label className="form-label">Sort Order</label><input className="form-input" type="number" min="1" value={form.sortOrder} onChange={e => setForm({ ...form, sortOrder: e.target.value })} placeholder="1 = first, 2 = second..." /><p style={{ fontSize: 11, color: 'var(--grey-mid)', marginTop: 5 }}>Leave blank to add at end.</p></div>
        <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">Image</label><input type="file" accept="image/*" ref={fileRef} onChange={handleFile} style={{ display: 'none' }} /><button className="btn-outline" onClick={() => fileRef.current.click()} style={{ width: '100%' }}>{imagePreview ? '↻ Change Image' : '↑ Upload Image'}</button>{imagePreview && <img src={imagePreview} alt="preview" style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 6, marginTop: 10 }} />}</div>
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Add Item'}</button>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
};

const BrandStoreManager = ({ slug }) => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', address: '' });
  const [saving, setSaving] = useState(false);

  const fetchStores = async () => {
    try { const snap = await getDocs(collection(db, 'brands', slug, 'stores')); setStores(snap.docs.map(d => ({ id: d.id, ...d.data() }))); } catch { setStores([]); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchStores(); }, [slug]);

  const openForm = (store = null) => { setEditing(store); setForm(store ? { name: store.name, email: store.email, address: store.address || '' } : { name: '', email: '', address: '' }); setShowForm(true); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    if (!form.email.trim()) { toast.error('Email required'); return; }
    setSaving(true);
    try {
      if (editing) { await updateDoc(doc(db, 'brands', slug, 'stores', editing.id), form); toast.success('Updated!'); }
      else { await addDoc(collection(db, 'brands', slug, 'stores'), form); toast.success('Store added!'); }
      fetchStores(); setShowForm(false);
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async (store) => {
    if (!window.confirm(`Delete "${store.name}"?`)) return;
    try { await deleteDoc(doc(db, 'brands', slug, 'stores', store.id)); toast.success('Deleted'); fetchStores(); } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="bap-section">
      <div className="bap-section-header"><div><h2>Stores</h2><p>{stores.length} stores</p></div><button className="btn-primary" onClick={() => openForm()}>+ Add Store</button></div>
      {showForm && (
        <div className="bap-form-card">
          <div className="bap-form-header"><h3>{editing ? 'Edit Store' : 'Add Store'}</h3><button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--grey-mid)' }}>×</button></div>
          <div className="bap-form-grid">
            <div className="form-group"><label className="form-label">Store Name *</label><input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sandwich Chefs Parramatta" /></div>
            <div className="form-group"><label className="form-label">Owner Email *</label><input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="owner@store.com.au" /></div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">Address</label><input className="form-input" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Shop address" /></div>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}><button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Add Store'}</button><button className="btn-outline" onClick={() => setShowForm(false)}>Cancel</button></div>
        </div>
      )}
      {loading ? <p style={{ color: 'var(--grey-mid)' }}>Loading...</p> : (
        <div className="bap-stores-list">
          {stores.map(store => (
            <div key={store.id} className="bap-store-card">
              <div className="bap-store-icon">🏪</div>
              <div className="bap-store-info"><h3>{store.name}</h3><p>📧 {store.email}</p>{store.address && <p>📍 {store.address}</p>}</div>
              <div style={{ display: 'flex', gap: 8 }}><button className="btn-outline" style={{ padding: '6px 12px', fontSize: 11 }} onClick={() => openForm(store)}>Edit</button><button className="btn-danger" onClick={() => handleDelete(store)}>Delete</button></div>
            </div>
          ))}
          {stores.length === 0 && <p className="bap-empty">No stores yet.</p>}
        </div>
      )}
    </div>
  );
};

const BrandEmailSettings = ({ slug }) => {
  const [config, setConfig] = useState({ serviceId: '', templateId: '', publicKey: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDocs(collection(db, 'brands', slug, 'settings'));
        const data = {};
        snap.docs.forEach(d => { data[d.id] = d.data().value; });
        setConfig({ serviceId: data.serviceId || '', templateId: data.templateId || '', publicKey: data.publicKey || '' });
      } catch {}
      setLoading(false);
    };
    fetch();
  }, [slug]);

  const handleSave = async () => {
    if (!config.serviceId || !config.templateId || !config.publicKey) { toast.error('All fields required'); return; }
    setSaving(true);
    try {
      await Promise.all([
        setDoc(doc(db, 'brands', slug, 'settings', 'serviceId'), { value: config.serviceId }),
        setDoc(doc(db, 'brands', slug, 'settings', 'templateId'), { value: config.templateId }),
        setDoc(doc(db, 'brands', slug, 'settings', 'publicKey'), { value: config.publicKey }),
      ]);
      toast.success('Email settings saved!');
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSaving(false);
  };

  return (
    <div className="bap-section">
      <div className="bap-section-header"><div><h2>Email Settings</h2><p>Configure EmailJS for this brand</p></div></div>
      <div className="bap-info-box">
        <h4>📧 EmailJS Setup</h4>
        <ol>
          <li>Go to <a href="https://emailjs.com" target="_blank" rel="noreferrer">emailjs.com</a> → create free account</li>
          <li>Add New Service → connect Gmail → note Service ID</li>
          <li>Create Template → set To: <code>{'{{store_email}}'}</code>, CC: <code>{'{{customer_email}}'}</code></li>
          <li>Use variables: <code>{'{{customer_name}}'}</code> <code>{'{{store_name}}'}</code> <code>{'{{items_list}}'}</code> <code>{'{{total_price}}'}</code> <code>{'{{comment}}'}</code></li>
          <li>Copy Template ID and Public Key from Account → paste below</li>
        </ol>
      </div>
      {loading ? <p>Loading...</p> : (
        <div className="bap-form-card" style={{ marginTop: 20 }}>
          <div className="form-group"><label className="form-label">Service ID</label><input className="form-input" value={config.serviceId} onChange={e => setConfig({ ...config, serviceId: e.target.value })} placeholder="service_xxxxxxx" /></div>
          <div className="form-group"><label className="form-label">Template ID</label><input className="form-input" value={config.templateId} onChange={e => setConfig({ ...config, templateId: e.target.value })} placeholder="template_xxxxxxx" /></div>
          <div className="form-group"><label className="form-label">Public Key</label><input className="form-input" value={config.publicKey} onChange={e => setConfig({ ...config, publicKey: e.target.value })} placeholder="Your public key" /></div>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
        </div>
      )}
    </div>
  );
};

export default BrandAdminPanel;
